import API_BASE_URL from "../config";
import { publicUrl } from "../publicOrigin";
import { detectCurrency } from "../currencyUtils";

// Thin fetch wrapper for /api/crm/* — throws with the server's message so
// pages can toast it directly.
export async function crmFetch(path, options = {}) {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.message || "Request failed");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const STAGES = [
  { key: "new", label: "New Enquiry", icon: "🌱", color: "#3b82f6" },
  { key: "contacted", label: "Contacted", icon: "📞", color: "#8b5cf6" },
  { key: "quote_sent", label: "Quote Sent", icon: "📄", color: "#f59e0b" },
  { key: "booked", label: "Booked", icon: "✅", color: "#10b981" },
  { key: "lost", label: "Lost", icon: "🚫", color: "#9ca3af" },
];

export const stageInfo = (key) => STAGES.find((s) => s.key === key) || STAGES[0];

export const ACTIVITY_TYPES = [
  { key: "call", label: "Call", icon: "📞" },
  { key: "whatsapp", label: "WhatsApp", icon: "💬" },
  { key: "email", label: "Email", icon: "✉️" },
  { key: "meeting", label: "Meeting", icon: "🤝" },
  { key: "note", label: "Note", icon: "📝" },
];

export const activityInfo = (key) =>
  ACTIVITY_TYPES.find((t) => t.key === key) || ACTIVITY_TYPES[4];

// Client-facing links carry the studio's own domain when they have a website
// (share_origin from the API), so a quote arrives from their brand rather than
// ours. Without one we fall back to publicUrl(), which is Photentra's origin —
// and never window.location on desktop, where that is app://photentra.
export function crmShareUrl(path, shareOrigin) {
  return shareOrigin ? `${shareOrigin}${path}` : publicUrl(path);
}

export const LEAD_SOURCES = [
  "Instagram", "Facebook", "Google", "WhatsApp", "Referral",
  "Walk-in", "Wedding fair", "Website", "Enquiry form", "Other",
];

export const QUOTE_STATUSES = {
  sent: { label: "Awaiting reply", icon: "📨", color: "#f59e0b" },
  accepted: { label: "Accepted", icon: "✅", color: "#10b981" },
  declined: { label: "Declined", icon: "🚫", color: "#dc2626" },
};

export const quoteStatus = (key) => QUOTE_STATUSES[key] || QUOTE_STATUSES.sent;

export const TEMPLATE_CHANNELS = [
  { key: "whatsapp", label: "WhatsApp", icon: "💬" },
  { key: "email", label: "Email", icon: "✉️" },
  { key: "sms", label: "SMS", icon: "📱" },
];

// Fills {{client_name}}-style placeholders from a lead (and its quote).
// Anything we have no value for is replaced with an empty string rather than
// left as literal braces the client would see.
export function renderTemplate(body, { lead, quote, quoteLink, studioName } = {}) {
  const values = {
    "{{client_name}}": lead?.client_name || "",
    "{{event_type}}": lead?.event_type || "",
    "{{event_date}}": formatDate(lead?.event_date) || "",
    "{{venue}}": lead?.venue || "",
    "{{quote_link}}": quoteLink || "",
    "{{quote_total}}": formatMoney(quote?.total) || "",
    "{{studio_name}}": studioName || localStorage.getItem("studioName") || "",
  };
  return Object.entries(values).reduce(
    (out, [key, val]) => out.split(key).join(val),
    body || ""
  );
}

const CURRENCY_SYMBOLS = {
  INR: "₹", USD: "$", EUR: "€", GBP: "£", AED: "د.إ", AUD: "A$", CAD: "C$", SGD: "S$",
};

export function formatMoney(amount) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return null;
  const currency = detectCurrency();
  const symbol = CURRENCY_SYMBOLS[currency] || "₹";
  const locale = currency === "INR" ? "en-IN" : "en-US";
  return `${symbol}${Math.round(n).toLocaleString(locale)}`;
}

// Event dates, follow-up dates and quote validity are calendar dates, not
// instants — the day does not change because the viewer is in another country.
// The API sends them as 'YYYY-MM-DD'; new Date() on that string would read it
// as UTC midnight and show the day before to anyone west of UTC. Build the
// date from its own parts instead.
export function calendarDate(d) {
  if (!d) return null;
  if (d instanceof Date) return isNaN(d) ? null : d;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(d));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const parsed = new Date(d);
  return isNaN(parsed) ? null : parsed;
}

export function formatDate(d) {
  const date = calendarDate(d);
  if (!date) return null;
  return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

export function formatDateTime(d) {
  if (!d) return null;
  const date = new Date(d);
  if (isNaN(date)) return null;
  return date.toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

// "in 3 days" / "today" / "2 days overdue" — the language of follow-ups.
export function dueLabel(dateStr) {
  const due = calendarDate(dateStr);
  if (!due) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / 86400000);
  if (diff === 0) return { text: "Today", tone: "today" };
  if (diff === 1) return { text: "Tomorrow", tone: "soon" };
  if (diff > 1) return { text: `In ${diff} days`, tone: diff <= 7 ? "soon" : "later" };
  if (diff === -1) return { text: "1 day overdue", tone: "overdue" };
  return { text: `${-diff} days overdue`, tone: "overdue" };
}

// yyyy-mm-dd for <input type="date"> values, from a DB date/timestamp.
export function toInputDate(d) {
  const date = calendarDate(d);
  if (!date) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}
